import React from 'react';
import { formatCurrency } from '../../../../../../../../../../utils/functions';
import './ModificationsSummary.scss';

interface ModificationData {
  modification_id: number;
  user_id: number;
  user_name: string;
  modification_detail: string;
  modification_amount: number;
  timestamp: string;
  isnegative: boolean;
}

interface ModificationsSummaryProps {
  modifications: ModificationData[];
}

interface UserModifications {
  user_name: string;
  count: number;
  total_amount: number;
  modifications: ModificationData[];
}

interface ItemPattern {
  detail: string;
  count: number;
  total_amount: number;
}

const ModificationsSummary: React.FC<ModificationsSummaryProps> = ({ modifications }) => {
  // Filter only negative modifications using the isnegative flag
  const negativeModifications = modifications.filter(m => m.isnegative === true);

  // Calculate total financial burden
  const totalBurden = negativeModifications.reduce((sum, m) => sum + Math.abs(m.modification_amount), 0);

  // Group by user
  const userModifications = negativeModifications.reduce((acc, mod) => {
    const userName = mod.user_name || 'Usuario Desconocido';
    if (!acc[userName]) {
      acc[userName] = {
        user_name: userName,
        count: 0,
        total_amount: 0,
        modifications: []
      };
    }
    acc[userName].count++;
    acc[userName].total_amount += Math.abs(mod.modification_amount);
    acc[userName].modifications.push(mod);
    return acc;
  }, {} as Record<string, UserModifications>);

  // Sort users by total amount descending
  const sortedUsers = Object.values(userModifications).sort((a, b) => b.total_amount - a.total_amount);

  // Detect patterns - group by modification detail
  const detailPatterns = negativeModifications.reduce((acc, mod) => {
    const detail = mod.modification_detail || 'Sin detalle';
    if (!acc[detail]) {
      acc[detail] = {
        detail,
        count: 0,
        total_amount: 0
      };
    }
    acc[detail].count++;
    acc[detail].total_amount += Math.abs(mod.modification_amount);
    return acc;
  }, {} as Record<string, ItemPattern>);

  // Get patterns with more than 1 occurrence, sorted by count
  const repeatedPatterns = Object.values(detailPatterns)
    .filter(p => p.count > 1)
    .sort((a, b) => b.count - a.count);

  if (negativeModifications.length === 0) {
    return (
      <div className="sb-section modifications-summary">
        <h3 className="sb-section-title">Modificaciones Negativas</h3>
        <div className="no-modifications">
          <p>✅ No se registraron modificaciones negativas en esta sesión</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sb-section modifications-summary">
      <h3 className="sb-section-title">Modificaciones Negativas</h3>
      
      {/* Financial Impact */}
      <div className="financial-impact">
        <div className="impact-card">
          <div className="impact-label">Impacto Financiero Total</div>
          <div className="impact-value negative">{formatCurrency(totalBurden)}</div>
        </div>
        <div className="impact-card">
          <div className="impact-label">Número de Modificaciones</div>
          <div className="impact-value">{negativeModifications.length}</div>
        </div>
      </div>

      {/* Responsible Users */}
      <div className="responsible-users">
        <h4 className="subsection-title">Responsables</h4>
        <div className="users-table">
          <div className="table-header">
            <div className="col-user">Usuario</div>
            <div className="col-count">Cantidad</div>
            <div className="col-amount">Monto Total</div>
          </div>
          {sortedUsers.slice(0, 5).map((user, idx) => (
            <div key={idx} className="table-row">
              <div className="col-user">{user.user_name}</div>
              <div className="col-count">{user.count}</div>
              <div className="col-amount">{formatCurrency(user.total_amount)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Repeated Patterns */}
      {repeatedPatterns.length > 0 && (
        <div className="repeated-patterns">
          <h4 className="subsection-title">Patrones Detectados</h4>
          <div className="patterns-list">
            {repeatedPatterns.slice(0, 5).map((pattern, idx) => (
              <div key={idx} className="pattern-item">
                <div className="pattern-detail">
                  <span className="pattern-badge">{pattern.count}x</span>
                  <span className="pattern-text">{pattern.detail}</span>
                </div>
                <div className="pattern-amount">{formatCurrency(pattern.total_amount)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ModificationsSummary;
